import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Icon, GoldButton, SectionHead, EmptyState } from "../ui";

const when = (d) => new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export function Thread({ api, it, role, onChange }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/property-desk/interests/${it.id}/reply`, { message: msg }); setMsg(""); toast.success("Reply relayed through the platform."); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send."); } finally { setBusy(false); }
  };
  return (
    <article className="border pd-line pd-surface p-6 md:p-8" data-testid={`pd-interest-${it.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="pd-label mb-1">{it.intent} · {it.status.replace("_", " ")} · {when(it.created_at)}</div>
          <h3 className="pd-serif text-2xl leading-tight">{it.slug ? <Link to={`/property-desk/p/${it.slug}`} className="hover:text-[#C9A84C] transition-colors">{it.property_title}</Link> : it.property_title}</h3>
          <div className="pd-muted text-xs mt-1">{role === "partner" ? (it.through_platform_only ? "Verified member · through platform" : `${it.member_name || "Member"} · ${it.member_email}${it.phone ? ` · ${it.phone}` : ""}`) : `To the Verified Property Partner`}</div>
        </div>
        {it.status === "new" && role === "partner" && <span className="pd-tag !border-[#C9A84C]">New</span>}
      </div>
      <div className="mt-5 space-y-3">
        {it.message && <Bubble from={role === "partner" ? "them" : "me"} by={it.member_name || "Member"} at={it.created_at} text={it.message} />}
        {(it.thread || []).map((m) => <Bubble key={m.id} from={(m.from === "partner") === (role === "partner") ? "me" : "them"} by={m.by} at={m.at} text={m.message} />)}
      </div>
      <div className="mt-5 flex gap-3">
        <input className="pd-input flex-1" placeholder={role === "partner" ? "Reply through the platform…" : "Write to the partner…"} value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && msg.trim().length > 1 && send()} data-testid={`pd-reply-input-${it.id}`} />
        <GoldButton className="!py-3 !px-5" disabled={busy || msg.trim().length < 2} onClick={send} testId={`pd-reply-send-${it.id}`}>Send <Icon.Arrow size={12} /></GoldButton>
      </div>
    </article>
  );
}

const Bubble = ({ from, by, at, text }) => (
  <div className={`max-w-[85%] ${from === "me" ? "ml-auto" : ""}`}>
    <div className={`px-4 py-3 text-sm leading-relaxed ${from === "me" ? "bg-[#C9A84C]/10 border border-[#C9A84C]/30" : "bg-white/[0.03] border border-white/10"}`}>{text}</div>
    <div className="pd-muted text-[10px] tracking-[0.14em] uppercase mt-1">{by} · {when(at)}</div>
  </div>
);

export default function InterestsPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState(null);
  const load = () => api.get("/property-desk/my-interests").then((r) => setRows(r.data.interests)).catch(() => setRows([]));
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="pt-28 md:pt-36 max-w-4xl mx-auto px-6 md:px-10 pb-20" data-testid="pd-my-interests-page">
      <SectionHead label="Your conversations" title="Expressions of interest." />
      {!rows ? <div className="pd-skeleton h-40" /> : !rows.length ? <EmptyState text="You have not yet expressed interest in a property. When you do, the conversation lives here." />
        : <div className="space-y-5">{rows.map((it) => <Thread key={it.id} api={api} it={it} role="member" onChange={load} />)}</div>}
    </div>
  );
}
